'use client'

import { useRouter, useSearchParams } from 'next/navigation'
import { useCallback } from 'react'

import { createLogger } from '@/lib/logger'

const LOGGER = createLogger('hooks/use-url-query-params')

type SetQueryParamsOptions = {
  /** Replace the current history entry instead of pushing a new one. */
  replace?: boolean
  /**
   * Update the URL via the native History API instead of `router.push`/`replace`,
   * so the change does NOT trigger an App Router server round-trip (no RSC fetch,
   * no server-component re-render). `useSearchParams` still reflects the change.
   *
   * Use for high-frequency, same-page query writes (e.g. the trade blotter search
   * and filters): an RSC navigation per keystroke is wasteful, and after a Vercel
   * deploy that navigation surfaces version skew and forces a full page reload.
   * App Router has no `{ shallow }` flag on `router.push` (that's Pages Router);
   * `window.history.pushState`/`replaceState` is the documented App Router
   * shallow-routing API — Next integrates them with `useSearchParams`.
   * @see https://nextjs.org/docs/app/guides/single-page-applications#shallow-routing-on-the-client
   */
  shallow?: boolean
}

type UseUrlQueryParamsConfig = {
  /** Default `shallow` for every `setQueryParams` call from this hook instance. */
  shallow?: boolean
}

// biome-ignore lint/style/noDefaultExport: Existing default export — prefer named exports for new code
export default function useUrlQueryParams<T>(
  config?: UseUrlQueryParamsConfig,
): {
  getQueryParams: () => T
  /** Note: This must never be called in the same callback as a dispatch call.
   * This is because the dispatches should happen in a useEffect that depends on the query params.
   * Otherwise, this can create downstream race conditions that are hard to debug.
   */
  setQueryParams: (
    newParams: Partial<T>,
    description?: string,
    options?: SetQueryParamsOptions,
  ) => void
} {
  const router = useRouter()
  const searchParams = useSearchParams()
  const defaultShallow = config?.shallow ?? false

  const getQueryParams = useCallback((): T => {
    const params: Partial<T> = {}
    searchParams.forEach((value, key) => {
      params[key as keyof T] = value as T[keyof T]
    })
    return params as T
  }, [searchParams])

  const setQueryParams = useCallback(
    (
      newParams: Partial<T>,
      description?: string,
      options?: SetQueryParamsOptions,
    ) => {
      LOGGER.debug('Setting query params', {
        newParams,
        description,
      })
      const params = new URLSearchParams(searchParams.toString())
      Object.entries(newParams).forEach(([key, value]) => {
        if (value === undefined) {
          params.delete(key)
        } else if (value === null) {
          params.set(key, 'null')
        } else {
          params.set(key, value.toString())
        }
      })
      // Calling params.toString() ensures everything is encoded correctly
      // https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams/toString
      // https://url.spec.whatwg.org/#urlsearchparams-stringification-behavior
      const nextSearch = params.toString()
      const url = `?${nextSearch}`

      // No-op if the new query string matches the current one. Without this
      // bailout, callers that fire on mount with the URL's current value (e.g.
      // a debounced Search seeded from `?searchTerm=`) would push a duplicate
      // history entry, making the browser back button take an extra press.
      if (nextSearch === searchParams.toString()) {
        return
      }

      const shallow = options?.shallow ?? defaultShallow

      // Shallow: change the URL without an App Router server round-trip. This is
      // Next's documented App Router shallow-routing pattern — it integrates
      // history.pushState/replaceState so `useSearchParams` still updates.
      // https://nextjs.org/docs/app/guides/single-page-applications#shallow-routing-on-the-client
      if (shallow) {
        if (options?.replace) {
          window.history.replaceState(null, '', url)
        } else {
          window.history.pushState(null, '', url)
        }
        return
      }

      // Default: client-side navigation (RSC round-trip).
      // Use replace instead of push to avoid creating extra history entries.
      if (options?.replace) {
        router.replace(url)
      } else {
        router.push(url)
      }
    },
    [router, searchParams, defaultShallow],
  )

  return { getQueryParams, setQueryParams }
}
